import { useEffect, useMemo, useRef, useState } from 'react';
import Plotly from 'plotly.js-dist-min';
import { AppBar, Box, Button, css, Select, Stack, TextField, Toolbar, Typography } from '@mui/material';
import { SimpleLinearRegression } from 'ml-regression-simple-linear';

// import { UseLayerDashboardStore } from '../stores/useLayerDashboardStore';
import { sortAttributesToFilter, sortAttributesToPlot } from '../utils/attributesUtil';
import { TitleChart } from './TitleChart';
import { useLayerDashboardTabStore } from '../context/LayerDashboardContext';

const styles = css({
    height: '100%',
    '.container-chart': {
        height: '100%',
        width: '100%'
    }
});

const boxes = css({
    marginRight: 5,
    marginBottom: 5
});

const stats = css({
    borderRadius: 5,
    padding: 10,
    backgroundColor: 'rgb(4, 31, 60, 0.6)',
    color: 'white'
});

const input = css({
    input: {
        padding: '8.5px',
    }
});

export const Scatterplot = () => {
    const { data, selectedData, displayableData, setDisplayableData, attributes, setSelectedData } = useLayerDashboardTabStore(state => ({
        displayableData: state.displayableData,
        setDisplayableData: state.setDisplayableData,
        attributes: state.attributes,
        setSelectedData: state.setSelectedData,
        selectedData: state.selectedData,
        data: state.data
    }));

    let scatterplotId = 'scatterplot-' + Math.random().toString(36).substring(2);

    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const [scatterData, setScatterData] = useState<any>([]);
    const [linearRegression, setLinearRegression] = useState<string>();
    const [logRegression, setLogRegression] = useState<string>();
    const [scatterXField, setScatterXField] = useState<string>('porosidade_perc_ncs');
    const [scatterYField, setScatterYField] = useState<string>('densidade_grao_gm_cc');
    const [scatterGroupField, setScatterGroupField] = useState<string>('no-group');
    const [chartTraces, setChartTraces] = useState<Plotly.Data[]>([]);
    const [minYValue, setMinYValue] = useState<number>(2.5);
    const [maxYValue, setMaxYValue] = useState<number>(4.5);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    //const graphDiv = useRef<any>();

    const resetDataFilter = () => {
        setDisplayableData(data);
    };

    const attributesPlotSorted = useMemo(() => {
        return sortAttributesToPlot(attributes);
    }, [JSON.stringify(attributes)]);

    const attributesFilterSorted = useMemo(() => {
        return sortAttributesToFilter(attributes);
    }, [JSON.stringify(attributes)]);

    function calculateLinearRegression(x: number[], y: number[]) {
        const n = x.length;
        const sumX = x.reduce((a, b) => a + b, 0);
        const sumY = y.reduce((a, b) => a + b, 0);
        const sumXY = x.reduce((acc, val, i) => acc + val * y[i], 0);
        const sumXX = x.reduce((acc, val) => acc + val * val, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        const combined = x.map((xi, i) => ({ x: xi, y: slope * xi + intercept }));
        combined.sort((a, b) => a.x - b.x);

        setLinearRegression(`f(x) = ${slope.toFixed(4)}x ${intercept > 0 ? '+ ' + intercept.toFixed(4) : ('- ' + (intercept * -1).toFixed(4))}`);

        return {
            x: combined.map(p => p.x),
            y: combined.map(p => p.y),
        };
    }

    const calculateLogRegression = (x: number[], y: number[], xType: string, yType: string) => {
        const validData = x.map((xi, i) => ({ x: xi, y: y[i] })).filter(p => p.x > 0);

        x = validData.map(v => Math.log(v.x));

        const n = x.length;
        const sumX = x.reduce((a, b) => a + b, 0);
        const sumY = y.reduce((a, b) => a + b, 0);
        const sumXY = x.reduce((acc, val, i) => acc + val * y[i], 0);
        const sumXX = x.reduce((acc, val) => acc + val * val, 0);

        const slope = (n * sumXY - sumX * sumY) / (n * sumXX - sumX * sumX);
        const intercept = (sumY - slope * sumX) / n;

        const combined = validData.map(p => {
            let xVal = Math.log(p.x);
            let yVal = slope * xVal + intercept;
            return { x: p.x, y: yVal };
        });

        combined.sort((a, b) => a.x - b.x);

        let formula = `y = ${slope.toFixed(4)} * ln(x) ${intercept >= 0 ? '+ ' : '- '}${intercept.toFixed(4)}`;
        setLogRegression(formula);

        return {
            x: combined.map(p => p.x),
            y: combined.map(p => p.y),
        };
    };

    const GetAxisType = (scatterField: string) => {
        let fieldType = attributes.find(x => x.Name === scatterField)?.AxisType;

        switch (fieldType) {
            case 1:
                return 'log';
            case 0:
            default:
                return 'linear';
        }
    };

    useEffect(() => {
        setScatterData(displayableData.map(x => ({
            x: x[scatterXField],
            y: x[scatterYField],
            group: scatterGroupField !== 'no-group' ? x[scatterGroupField] : 'Scatter Data',
            dashboardpointidentifier: x['dashboardpointidentifier'],
        })));

    }, [displayableData, scatterXField, scatterYField, scatterGroupField]);

    useEffect(() => {
        if (scatterData.length > 0) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const minValue = Math.floor(Math.min(...scatterData.map((x: { y: number; }) => x.y)) * 0.9);
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const maxValue = Math.ceil(Math.max(...scatterData.map((x: { y: number; }) => x.y)) * 1.1);
            setMinYValue(minValue);
            setMaxYValue(maxValue);
        }
    }, [scatterData]);

    useEffect(() => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const ensuredData = scatterData.filter((el: any) => typeof (el.x) === 'number' && typeof (el.y) === 'number');
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allXValues = ensuredData.map((el: any) => el.x!);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const allYValues = ensuredData.map((el: any) => el.y!);
        const traces = [];
        if (scatterGroupField && scatterGroupField !== 'no-group') {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const allGroups = ensuredData.map((x: any) => x.group);
            const distinctGroups = Array.from(new Set(allGroups));
            distinctGroups.forEach((group, idx) => {
                const traceData: Plotly.Data = {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    x: ensuredData.filter((x: any) => x.group === group).map((el: any) => el.x!),
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    y: ensuredData.filter((x: any) => x.group === group).map((el: any) => el.y!),
                    mode: 'markers',
                    type: 'scatter',
                    name: group ? group?.toString() : 'Without ' + attributes.find(x => x.Name === scatterGroupField)?.Label,
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    customdata: [ensuredData.filter((x: any) => x.group === group).map((el: any) => [el.dashboardpointidentifier])]
                };
                traces.push(traceData);
            });
        } else {
            const traceData: Plotly.Data = {
                x: allXValues,
                y: allYValues,
                mode: 'markers',
                type: 'scatter',
                name: 'Scatter Data',
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                customdata: [ensuredData.map((el: any) => [el.dashboardpointidentifier])]
            };
            traces.push(traceData);
        }

        let regressionLine = calculateLinearRegression(allXValues, allYValues);
        const regressionTrace: Plotly.Data = {
            x: regressionLine.x,
            y: regressionLine.y,
            mode: 'lines',
            type: 'scatter',
            name: 'Linear Regression',
        };
        traces.push(regressionTrace);

        const logLine = calculateLogRegression(allXValues, allYValues, GetAxisType(scatterXField), GetAxisType(scatterYField));
        const regressionLogTrace: Plotly.Data = {
            x: logLine.x,
            y: logLine.y,
            mode: 'lines',
            type: 'scatter',
            name: 'Logarithmic Regression',
        };
        traces.push(regressionLogTrace);

        setChartTraces(traces);

        const data = traces;

        const render = async () => {
            const plotDiv = await Plotly.newPlot(scatterplotId, data, {
                autosize: true,
                margin: {
                    l: 25,
                    r: 25,
                    b: 25,
                    t: 25,
                    pad: 5
                },
                yaxis: {
                    type: GetAxisType(scatterYField),
                    automargin: true,
                    range: GetAxisType(scatterYField) === 'log' ? [Math.log10(minYValue), Math.log10(maxYValue)] : [minYValue, maxYValue],
                    title: {
                        text: attributes.find(x => x.Name === scatterYField)?.Label,
                        font: { size: 18 }
                    },
                },
                xaxis: {
                    type: GetAxisType(scatterXField),
                    automargin: true,
                    title: {
                        text: attributes.find(x => x.Name === scatterXField)?.Label,
                        font: { size: 18 }
                    },
                }
            }, {
                autosizable: true,
                responsive: true,
                displayModeBar: true,
                displaylogo: false,
                modeBarButtonsToRemove: ['zoomIn2d', 'zoomOut2d', 'autoScale2d']
            });
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            plotDiv?.on('plotly_selected', function (eventData: any) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const ensuredData = displayableData.filter((el: any) => typeof (el[scatterXField]) === 'number' && typeof (el[scatterYField]) === 'number');
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const allSelectedIdentifiers: any = [];
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                eventData.points.forEach((selectedPoint: any) => {
                    const customData = selectedPoint.data.customdata[0][selectedPoint.pointNumber];
                    allSelectedIdentifiers.push(customData[0]);
                });
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                const locatedData = ensuredData.filter((x: any, idx: number) => {
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    return allSelectedIdentifiers?.find((id: number) => id === x.dashboardpointidentifier);
                });
                setSelectedData(locatedData);
            });
        };
        render();
    }, [scatterData]);

    useEffect(() => {
        if (selectedData) {
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const ensuredData = displayableData.filter((el: any) => typeof (el[scatterXField]) === 'number' && typeof (el[scatterXField]) === 'number');
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const identifiers = selectedData.map(x => x.dashboardpointidentifier);

            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const updateMarkers: any = [];
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            const traceIndexdsToUpdate: any = [];
            //customdata: [ensuredData.filter((x:any) => x.group === group).map((el:any) => [el.dashboardpointidentifier])]
            // eslint-disable-next-line @typescript-eslint/no-explicit-any
            chartTraces.forEach((trace: any, traceIndex: number) => {
                if (trace.customdata) {
                    let opacities = [];
                    for (let i = 0; i < trace.customdata[0].length; i++) {
                        opacities.push(0.1);
                    }

                    //const indexesIdentifiers = identifiers.map((id:number) => trace.customdata[0].findIndex((x:any) => x[0] === id));
                    identifiers.forEach((id: number) => {
                        // eslint-disable-next-line @typescript-eslint/no-explicit-any
                        const indexIdentifier = trace.customdata[0].findIndex((x: any) => x[0] === id);
                        opacities[indexIdentifier] = 1;
                    });
                    if (identifiers.length > 0) {
                        let update = { marker: { opacity: opacities } };
                        updateMarkers.push(update);
                        traceIndexdsToUpdate.push(traceIndex);
                    }
                }
            });

            for (let x = 0; x < updateMarkers.length; x++) {
                Plotly.restyle(scatterplotId, updateMarkers[x], [traceIndexdsToUpdate[x]]);
            }
        }
    }, [selectedData]);

    useEffect(() => {
        Plotly.relayout(scatterplotId, {
            yaxis: {
                type: GetAxisType(scatterYField),
                automargin: true,
                range: GetAxisType(scatterYField) === 'log' ? [Math.log10(minYValue), Math.log10(maxYValue)] : [minYValue, maxYValue],
                title: {
                    text: attributes.find(x => x.Name === scatterYField)?.Label,
                    font: { size: 18 }
                },
            }
        });
    }, [minYValue, maxYValue]);

    return (
        <Stack direction='column' css={styles}>
            <TitleChart title='Scatter' maximizeOperation={'scatterplot'} />
            <Stack direction='column' justifyContent={'space-between'}>
                <Stack direction='row' >
                    <Stack direction='row'>
                        <Box css={boxes}>
                            <Box css={boxes}>
                                Axis X
                            </Box>
                            <Select size='small' native
                                value={scatterXField}
                                onChange={(evt) => setScatterXField(evt.target.value)}
                            >
                                {attributesPlotSorted.map(x => <option key={'scatter-attr-x-' + x.Id} value={x.Name}>{x.Label}</option>)}
                            </Select>
                        </Box>
                        <Box css={boxes}>
                            <Box css={boxes}>
                                Axis Y
                            </Box>
                            <Select size='small' native
                                value={scatterYField}
                                onChange={(evt) => setScatterYField(evt.target.value)}
                            >
                                {attributesPlotSorted.map(x => <option key={'scatter-attr-x-' + x.Id} value={x.Name}>{x.Label}</option>)}
                            </Select>
                        </Box>
                    </Stack>
                    <Box css={boxes}>
                        <Box css={boxes}>
                            Data Group
                        </Box>
                        <Select size='small' native
                            value={scatterGroupField}
                            onChange={(evt) => setScatterGroupField(evt.target.value)}
                        >
                            <option value='no-group'>No Group</option>
                            {attributesFilterSorted.map(x => <option key={'scatter-attr-x-' + x.Id} value={x.Name}>{x.Label}</option>)}
                        </Select>
                    </Box>
                    <Box css={boxes}>
                        <Box css={boxes}>
                            Min Y Value
                        </Box>
                        <TextField size='small' value={minYValue} type='number' onChange={(evt) => setMinYValue(parseFloat(evt.target.value) < 0 ? 1 : parseFloat(evt.target.value))}></TextField>
                    </Box>
                    <Box css={boxes}>
                        <Box css={boxes}>
                            Max Y Value
                        </Box>
                        <TextField size='small' value={maxYValue} type='number' onChange={(evt) => setMaxYValue(parseFloat(evt.target.value) < 0 ? 1 : parseFloat(evt.target.value))}></TextField>
                    </Box>
                </Stack>
                <Stack direction={'row'} css={stats} justifyContent={'space-between'}>
                    {/* <Box>
                        <strong>Coefficients</strong>: {linearRegression?.coefficients[0]?.toFixed(2)}, {linearRegression?.coefficients[1]?.toFixed(2)}
                    </Box> */}
                    <Box>
                        <strong>Linear</strong>: {linearRegression}
                    </Box>
                    <Box>
                        <strong>Logarithimic</strong>: {logRegression}
                    </Box>
                    {/* <Box>
                        <strong>Intercept</strong>: {linearRegression?.intercept?.toFixed(2)}
                    </Box>
                    <Box>
                        <strong>Slope</strong>: {linearRegression?.slope?.toFixed(2)}
                    </Box> */}
                </Stack>
            </Stack>
            <Stack direction='row' flexGrow={1}>
                <div id={scatterplotId} className='container-chart'></div>
            </Stack>
        </Stack>
    );
};